import type { CalendarEventDto, EventReservationDto, EventScope } from '../../api/calendar';
import { tCalendar } from '../../lib/calendar-i18n';

/**
 * 일정을 지울 때 함께 물을 회의실 예약 (2026-10-23 사용자 지시).
 *
 * 사용자 원문: 「회의실을 예약하고 일정을 취소했을 때, 회의실 예약 신청을 취소하시겠습니까? 혹은
 *   회의실이 예약 완료됐는데 삭제했을 경우 예약을 취소하시겠습니까? 물어서 삭제할 수 있는 기능」
 *
 * ⚠ 서버는 일정을 지워도 예약을 취소하지 않는다(일정은 soft delete) ⇒ 묻고, «예»면 화면이 하나씩 취소한다.
 * ⭐ 범위 규칙은 AOS `calRoomsForDelete` 와 같다 — 전체 = 전부 · 이 일정만 = 그 회차 · 이후 모두 = 그 회차부터 뒤.
 *   회차를 모르는 예약은 「전체」에서만 센다(모르는 것을 취소하지 않는다).
 * ⚠ 화면이 아는 예약은 «불러온 회차»의 것뿐이다 — 반복 일정의 「이후 모두」·「전체」는 화면 밖 회차의 방을 모른다.
 *   그래서 그때는 물음에 「다른 회차는 나의 예약에서」 한 줄을 붙인다.
 */
export function roomsForDelete(
  e: CalendarEventDto,
  scope: EventScope,
  loaded: readonly CalendarEventDto[],
): EventReservationDto[] {
  const seen = new Map<string, EventReservationDto>();
  for (const ev of [e, ...loaded]) {
    if (ev.id !== e.id) continue;
    for (const r of ev.reservations ?? []) if (!seen.has(r.id)) seen.set(r.id, r);
  }
  const all = [...seen.values()];
  if (scope === 'all') return all;
  const occ = e.occurrenceAt ? Date.parse(e.occurrenceAt) : NaN;
  if (Number.isNaN(occ)) return [];
  return all.filter((r) => {
    const at = r.occurrenceAt ? Date.parse(r.occurrenceAt) : NaN;
    if (Number.isNaN(at)) return false;
    return scope === 'this' ? at === occ : at >= occ;
  });
}

/** 하나라도 «예약 완료»면 「예약을 취소」, 전부 승인 대기면 「예약 신청을 취소」로 묻는다. */
export function roomDeleteAsk(rooms: readonly EventReservationDto[]): 'approved' | 'pending' {
  return rooms.some((r) => r.status === 'approved') ? 'approved' : 'pending';
}

/** 물음 팝업의 본문 · 제목 · 단추 글자. */
export function roomDeleteQuestion(
  rooms: readonly EventReservationDto[],
  scope: EventScope,
  recurring: boolean,
): { message: string; title: string; yes: string; no: string } {
  const kind = roomDeleteAsk(rooms);
  const lines = rooms.map((r) => {
    const name = r.resourceName || tCalendar('calendar.delete.room_fallback_name');
    const st = r.status === 'approved' ? tCalendar('calendar.delete.room_status_approved') : tCalendar('calendar.delete.room_status_pending');
    return `${name} · ${st}`;
  });
  const parts = [tCalendar(kind === 'approved' ? 'calendar.delete.rooms_ask_approved' : 'calendar.delete.rooms_ask_pending'), lines.join('\n')];
  if (recurring && scope !== 'this') parts.push(tCalendar('calendar.delete.rooms_other_occurrences'));
  return {
    message: parts.join('\n\n'),
    title: tCalendar('calendar.delete.rooms_ask_title'),
    yes: tCalendar(kind === 'approved' ? 'calendar.delete.rooms_yes_approved' : 'calendar.delete.rooms_yes_pending'),
    no: tCalendar(kind === 'approved' ? 'calendar.delete.rooms_no_approved' : 'calendar.delete.rooms_no_pending'),
  };
}

/** 일정을 지운 «뒤» 예약을 하나씩 취소한다 — 된 수를 돌려준다(하나가 실패해도 나머지는 간다 · AOS 와 같다). */
export async function cancelRoomsAfterDelete(
  rooms: readonly EventReservationDto[],
  cancel: (id: string) => Promise<unknown>,
): Promise<number> {
  let done = 0;
  for (const r of rooms) {
    try { await cancel(r.id); done += 1; } catch { /* 남은 것은 안내로 알린다 */ }
  }
  return done;
}
